import { z } from 'zod'
import { oc } from '@orpc/contract'
import { BILLING_ENTITY_VALUES } from '../billing-entity.contract'
import {
  BILLING_STATUS_VALUES,
  MANUAL_BILLING_STATUS_VALUES,
} from '../../care-coordination/dto/billing-status.contract'

// The shared V2 case wire shape, reused by the list/detail endpoints. Dates cross the
// boundary as ISO strings (pattern: toTaskDto). claimStatus/billingEntity are validated
// against their canonical value sets (contract-file imports, browser-safe); the other
// ledger codes stay plain strings — their value sets live in billing-v2-shared.ts, which
// must not cross the web package boundary.
export const billingCaseDtoSchema = z.object({
  id: z.number(),
  encounterId: z.string(),
  patientId: z.string(),
  appointmentId: z.string().nullable(),
  claimStatus: z.enum(BILLING_STATUS_VALUES),
  paymentStatus: z.string(),
  billingEntity: z.enum(BILLING_ENTITY_VALUES).nullable(),
  origin: z.string(),
  dos: z.string().nullable(),
  providerChargeCents: z.number().nullable(),
  allowedCents: z.number().nullable(),
  patientRespCents: z.number().nullable(),
  adjustmentCents: z.number().nullable(),
  expectedInsuranceCents: z.number().nullable(),
  primaryCode: z.string().nullable(),
  serviceLineCount: z.number(),
  dxCount: z.number(),
  encounterStatus: z.string().nullable(),
  appointmentStatus: z.string().nullable(),
  submissionRoute: z.string(),
  /**
   * External claim id from the submission plugin. NON-NULL DOES NOT MEAN ALREADY
   * SUBMITTED — `claimSubmittedAt` is the done marker. A non-null `externalClaimId` with a
   * still-null `claimSubmittedAt` is a normal, resumable mid-chain state: the next
   * submission run continues from here rather than creating a duplicate claim.
   */
  externalClaimId: z.string().nullable(),
  claimSubmittedAt: z.string().nullable(),
  /** Last submission failure reason shown on the case; cleared on success. */
  submitLastError: z.string().nullable(),
})

export type BillingCaseDto = z.infer<typeof billingCaseDtoSchema>

export const billingV2UpdateCase = oc
  .route({ method: 'POST', path: '/v2/billing/case/update' })
  .input(
    z.object({
      encounterId: z.string(),
      /** Only the manual subset — pending/ready-to-bill/paid are automatic transitions. */
      claimStatus: z.enum(MANUAL_BILLING_STATUS_VALUES).optional(),
      billingEntity: z.enum(BILLING_ENTITY_VALUES).optional(),
      /** User-picked "billing date" stamped into the legacy updated-at extension; defaults to now. */
      billingUpdateDate: z.iso.datetime().optional(),
    }),
  )
  .output(z.object({ case: billingCaseDtoSchema }))
